// MapComponent.js - Updated to use regions context
import { useEffect, useState, useCallback } from "react";
import ALPRMarkers from "./ALPRMarkers";
import {
  MapContainer,
  TileLayer,
  useMap,
  GeoJSON,
  Marker,
  Popup,
} from "react-leaflet";
import { useRegions } from "../../context/RegionsContext";
import osm2geojson from "osm2geojson-lite";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix for default markers in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Custom icon creator for site markers
const createSiteIcon = (isSelected = false) => {
  return L.divIcon({
    className: "custom-div-icon",
    html: `
        <div style="
          background-color: #ffffffff;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px solid ${isSelected ? "#3B82F6" : "white"};
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        ">
          <svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 128 128"><path fill="#424242" d="M83.05 124h-38.1c-5.26 0-9.53-4.27-9.53-9.53V13.53c0-5.26 4.27-9.53 9.53-9.53h38.11c5.26 0 9.53 4.27 9.53 9.53v100.94c-.01 5.26-4.27 9.53-9.54 9.53z"/><circle cx="64" cy="102.92" r="13.41" fill="#4CAF50"/><path fill="#6FD86F" d="M54.64 104.09c-1.14-.36-1.45-4.36 1.22-7.57c3.98-4.79 9.71-4.67 10.32-2.12c.85 3.57-3.67 2.16-7.11 5.04c-2.15 1.8-2.62 5.22-4.43 4.65z"/><circle cx="64" cy="66.21" r="13.41" fill="#FFCA28"/><path fill="#FFF59D" d="M54.64 67.39c-1.14-.36-1.45-4.36 1.22-7.57c3.98-4.79 9.71-4.67 10.32-2.12c.85 3.57-3.67 2.16-7.11 5.04c-2.15 1.8-2.62 5.22-4.43 4.65z"/><circle cx="64" cy="29.5" r="13.41" fill="#F44336"/><path fill="#FF8155" d="M54.64 30.68c-1.14-.36-1.45-4.36 1.22-7.57c3.98-4.79 9.71-4.67 10.32-2.12c.85 3.57-3.67 2.16-7.11 5.04c-2.15 1.8-2.62 5.22-4.43 4.65z"/><path fill="#757575" d="M49.48 23.12c-.47.92-1.89.88-1.76-.52c.32-3.41 2.26-6.09 3.85-7.73c3.16-3.32 7.8-5.21 12.43-5.2c4.63-.02 9.27 1.88 12.44 5.19c1.59 1.64 3.85 5.3 3.86 7.85c0 1.11-1.26 1.41-1.72.51c-1.15-2.24-4.71-9.55-14.57-9.55c-9.88 0-13.39 7.23-14.53 9.45zm0 73.5c-.47.92-1.89.88-1.76-.52c.32-3.41 2.26-6.09 3.85-7.73c3.16-3.32 7.8-5.21 12.43-5.2c4.63-.02 9.27 1.88 12.44 5.19c1.59 1.64 3.85 5.3 3.86 7.85c0 1.11-1.26 1.41-1.72.51c-1.15-2.24-4.71-9.55-14.57-9.55c-9.88 0-13.39 7.24-14.53 9.45zm0-36.75c-.47.92-1.89.88-1.76-.52c.32-3.41 2.26-6.09 3.85-7.73c3.16-3.32 7.8-5.21 12.43-5.2c4.63-.02 9.27 1.88 12.44 5.19c1.59 1.64 3.85 5.3 3.86 7.85c0 1.11-1.26 1.41-1.72.51c-1.15-2.24-4.71-9.55-14.57-9.55s-13.39 7.23-14.53 9.45z"/></svg>
        </div>
      `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

function MapController({ center, zoom, onMapReady }) {
  const map = useMap();

  useEffect(() => {
    if (center && zoom) {
      map.setView([center.lat, center.lng], zoom);
    }
  }, [center, zoom, map]);

  useEffect(() => {
    if (onMapReady) {
      onMapReady(map);
    }
  }, [map, onMapReady]);

  return null;
}

export default function MapComponent({
  selectedSystemId,
  useGlobalRegion = false,
  alprMarkers = [], // ADD THIS
  onMarkerClick = null, // ADD THIS
  hideDefaultMarkers = false, // ADD THIS
}) {
  const { regions, allSystems, isLoading, error, selectedRegion } =
    useRegions();

  const [currentRegion, setCurrentRegion] = useState(null);
  const [cityBoundary, setCityBoundary] = useState(null);
  const [geoJson, setGeoJson] = useState(null);
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [selectedSystemIds, setSelectedSystemIds] = useState(new Set());
  const [mapCenter, setMapCenter] = useState({ lat: 42.3601, lng: -71.0589 }); // Default to Boston
  const [zoomLevel, setZoomLevel] = useState(13);
  const [mapInstance, setMapInstance] = useState(null);

  // Initialize with first region when data is loaded
  useEffect(() => {
    if (!isLoading && regions.length > 0 && !currentRegion) {
      const firstRegion =
        regions.find((r) => r.region_id !== "all") ||
        regions[0];
      setCurrentRegion(firstRegion);

      if (firstRegion && firstRegion.region_id !== "all") {
        const lat = parseFloat(firstRegion.region_center_latitude);
        const lng = parseFloat(firstRegion.region_center_longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          setMapCenter({ lat, lng });
        }
      }
    }
  }, [regions, isLoading]);

  // NEW: Sync with global selectedRegion when useGlobalRegion is true
  useEffect(() => {
    if (useGlobalRegion && selectedRegion) {
      setCurrentRegion(selectedRegion);

      // Update map center and zoom for the selected region
      if (selectedRegion.region_id !== "all") {
        const lat = parseFloat(selectedRegion.region_center_latitude);
        const lng = parseFloat(
          selectedRegion.region_center_longitude,
        );
        if (!isNaN(lat) && !isNaN(lng)) {
          setMapCenter({ lat, lng });
          setZoomLevel(13);
        }
      }
    }
  }, [useGlobalRegion, selectedRegion]);

  // Load boundary data when region is set
  useEffect(() => {
    async function loadBoundary() {
      // Reset boundary when region changes
      setGeoJson(null);
      setCityBoundary(null);
      if (
        !currentRegion ||
        currentRegion.region_id === "all"
      ) {
        return;
      }

      if (currentRegion?.region_boundary) {
        try {
          const relId = currentRegion.region_boundary;
          const url = `https://overpass-api.de/api/interpreter?data=[out:json];relation(${relId});out geom;`;

          const osmRes = await fetch(url);
          const osmData = await osmRes.json();
          const converted = osm2geojson(osmData);
          setGeoJson(converted);

          if (
            osmData.elements &&
            osmData.elements[0] &&
            osmData.elements[0].bounds
          ) {
            setCityBoundary(osmData.elements[0].bounds);
          }
        } catch (err) {
          console.error("Error loading boundary for region:", err);
        }
      }
    }

    loadBoundary();
  }, [currentRegion]);

  // Update selected site when selectedSiteId changes
  useEffect(() => {
    if (selectedSystemId && allSystems.length > 0) {
      const system = allSystems.find(
        (s) => s.system_id.toString() === selectedSystemId.toString(),
      );
      if (system) {
        setSelectedSystem(system);
        const lat = parseFloat(system.system_latitude);
        const lng = parseFloat(system.system_longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          setMapCenter({ lat, lng });
          setZoomLevel(18);
        }
      }
    }
  }, [selectedSystemId, allSystems]);

  const handleSiteClick = useCallback((system) => {
    setSelectedSystem(system);
    const lat = parseFloat(system.system_latitude);
    const lng = parseFloat(system.system_longitude);

    if (!isNaN(lat) && !isNaN(lng)) {
      setMapCenter({ lat, lng });
      setZoomLevel(20);
    }

    setSelectedSystemIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(system.system_id)) {
        newSet.delete(system.system_id);
      } else {
        newSet.add(system.system_id);
      }
      return newSet;
    });
  }, []);

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p className="text-gray-500 text-sm">Loading map...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <p className="text-red-500 text-sm">Error loading map data</p>
      </div>
    );
  }

  if (!currentRegion) {
    return null;
  }

  const bounds =
    cityBoundary &&
    !isNaN(parseFloat(cityBoundary.minlat)) &&
    !isNaN(parseFloat(cityBoundary.minlon)) &&
    !isNaN(parseFloat(cityBoundary.maxlat)) &&
    !isNaN(parseFloat(cityBoundary.maxlon))
      ? [
          [parseFloat(cityBoundary.minlat), parseFloat(cityBoundary.minlon)],
          [parseFloat(cityBoundary.maxlat), parseFloat(cityBoundary.maxlon)],
        ]
      : null;

  return (
    <MapContainer
      center={[mapCenter.lat, mapCenter.lng]}
      zoom={zoomLevel}
      className="h-full w-full"
      maxBounds={bounds}
      zoomControl={true}
      style={{ overflow: "hidden" }}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution="&copy; OpenStreetMap contributors"
        maxZoom={22}
        maxNativeZoom={19}
      />

      {geoJson && (
        <GeoJSON
          data={geoJson}
          style={{ color: "red", weight: 3, fillOpacity: 0 }}
        />
      )}

      {/* Site Markers */}
      {/* Show ALPR markers if available, otherwise show default site markers */}
      {alprMarkers && alprMarkers.length > 0 ? (
        <ALPRMarkers markers={alprMarkers} onMarkerClick={onMarkerClick} />
      ) : (
        !hideDefaultMarkers &&
        allSystems.map((sys) => {
          const lat = parseFloat(sys.system_latitude);
          const lng = parseFloat(sys.system_longitude);

          if (isNaN(lat) || isNaN(lng)) {
            return null;
          }

          const isSelected =
            selectedSystemId &&
            sys.system_id.toString() === selectedSystemId.toString();

          return (
            <Marker
              key={sys.composite_key}
              position={[lat, lng]}
              icon={createSiteIcon(isSelected)}
              eventHandlers={{
                click: () => handleSiteClick(sys),
              }}
            >
              <Popup>
                <div className="p-2 min-w-[200px]">
                  <h3 className="font-semibold text-sm mb-2">
                    {sys.system_street}
                  </h3>
                  <div className="space-y-1 text-xs text-gray-600">
                    <div>Type: {sys.system_type}</div>
                    <div>Region: {sys.system_region}</div>
                    <div>Timezone: {sys.system_timezone}</div>
                    <div className="font-mono text-[10px] text-gray-400 mt-2">
                      {lat.toFixed(6)}, {lng.toFixed(6)}
                    </div>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })
      )}

      {/* Map Controller */}
      <MapController
        center={mapCenter}
        zoom={zoomLevel}
        bounds={cityBoundary}
        onMapReady={setMapInstance}
      />
    </MapContainer>
  );
}
